'use client';

import { useCallback, useRef } from 'react';

import { generateId } from 'ai';
import { BroadcastChannel } from 'broadcast-channel';
import { useEffectOnce } from 'react-use';

import type { SyncEvent, SyncEventPayload, SyncEventType } from '@/lib/sync/events';

const BROADCAST_CHANNEL_NAME = 'app-sync';
const TAB_ID = generateId();

type EventHandler<T extends SyncEventType> = (
  payload: SyncEventPayload[T],
  event: SyncEvent<T>,
) => void;

type StoredEventHandler = EventHandler<SyncEventType>;

/**
 * Cross-tab sync via BroadcastChannel API with type-safe events.
 * Each tab has unique ID and ignores self-emitted events.
 */
export function useCrossTabSync() {
  const channelRef = useRef<BroadcastChannel | null>(null);
  const handlersRef = useRef<Map<SyncEventType, Set<StoredEventHandler>>>(new Map());

  useEffectOnce(() => {
    if (typeof window === 'undefined') {
      return;
    }

    let channel: BroadcastChannel;
    try {
      channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    } catch {
      // BroadcastChannel is blocked in some sandboxed iframe contexts (e.g. Firefox in third-party embeds).
      // Silently degrade — cross-tab sync is unavailable but all other functionality continues.
      return;
    }

    channelRef.current = channel;

    channelRef.current.onmessage = (syncEvent: SyncEvent) => {
      if (syncEvent.tabId === TAB_ID) {
        return;
      }

      const handlers = handlersRef.current.get(syncEvent.type);
      if (handlers) {
        handlers.forEach(handler => {
          handler(syncEvent.payload, syncEvent);
        });
      }
    };

    return () => {
      channelRef.current?.close();
      channelRef.current = null;
    };
  });

  const emit = useCallback(<T extends SyncEventType>(type: T, payload: SyncEventPayload[T]) => {
    if (!channelRef.current) {
      return;
    }

    const event: SyncEvent<T> = {
      type,
      payload,
      timestamp: Date.now(),
      tabId: TAB_ID,
    };

    channelRef.current.postMessage(event);
  }, []);

  const on = useCallback(<T extends SyncEventType>(type: T, handler: EventHandler<T>) => {
    if (!handlersRef.current.has(type)) {
      handlersRef.current.set(type, new Set());
    }

    const handlers = handlersRef.current.get(type)!;
    handlers.add(handler as StoredEventHandler);

    return () => {
      handlers.delete(handler as StoredEventHandler);
      if (handlers.size === 0) {
        handlersRef.current.delete(type);
      }
    };
  }, []);

  return {
    emit,
    on,
    tabId: TAB_ID,
  };
}
